import { supabase } from '../lib/supabase';

export const helperService = {
  /**
   * Get all helpers with optional filters
   */
  async getHelpers(filters = {}) {
    try {
      let query = supabase?.from('helpers')?.select('*')?.order('rating', { ascending: false });

      // Apply filters
      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }
      if (filters?.expertise) {
        query = query?.contains('expertise', [filters?.expertise]);
      }
      if (filters?.language) {
        query = query?.contains('languages', [filters?.language]);
      }
      if (filters?.availabilityStatus) {
        query = query?.eq('availability_status', filters?.availabilityStatus);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      const helpers = data?.map(helper => ({
        id: helper?.id,
        userId: helper?.user_id,
        name: helper?.name,
        type: helper?.type,
        typeLabel: helper?.type_label,
        profileImage: helper?.profile_image,
        profileImageAlt: helper?.profile_image_alt,
        bio: helper?.bio,
        expertise: helper?.expertise,
        languages: helper?.languages,
        location: helper?.location,
        rating: helper?.rating,
        reviewCount: helper?.review_count,
        sessionsCompleted: helper?.sessions_completed,
        availabilityStatus: helper?.availability_status,
        verified: helper?.verified,
        verificationStatus: helper?.verification_status,
        workloadStatus: helper?.workload_status,
        sessionsThisMonth: helper?.sessions_this_month,
        avgRating: helper?.avg_rating,
        credentialExpiry: helper?.credential_expiry,
        lastActive: helper?.last_active
      })) || [];

      return { data: helpers, error: null };
    } catch (error) {
      console.error('Get helpers error:', error);
      return { data: [], error };
    }
  },

  /**
   * Get single helper by ID
   */
  async getHelperById(helperId) {
    try {
      const { data, error } = await supabase?.from('helpers')?.select('*')?.eq('id', helperId)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      const helper = {
        id: data?.id,
        userId: data?.user_id,
        name: data?.name,
        type: data?.type,
        typeLabel: data?.type_label,
        profileImage: data?.profile_image,
        profileImageAlt: data?.profile_image_alt,
        bio: data?.bio,
        expertise: data?.expertise,
        languages: data?.languages,
        location: data?.location,
        rating: data?.rating,
        reviewCount: data?.review_count,
        sessionsCompleted: data?.sessions_completed,
        availabilityStatus: data?.availability_status,
        verified: data?.verified
      };

      return { data: helper, error: null };
    } catch (error) {
      console.error('Get helper error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get helper availability slots
   */
  async getHelperAvailability(helperId, dayOfWeek = null) {
    try {
      let query = supabase?.from('helper_availability')?.select('*')?.eq('helper_id', helperId)?.eq('is_available', true)?.order('time_slot', { ascending: true });

      if (dayOfWeek !== null) {
        query = query?.eq('day_of_week', dayOfWeek);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      const slots = data?.map(slot => ({
        id: slot?.id,
        helperId: slot?.helper_id,
        dayOfWeek: slot?.day_of_week,
        timeSlot: slot?.time_slot,
        duration: slot?.duration,
        sessionType: slot?.session_type,
        isAvailable: slot?.is_available
      })) || [];

      return { data: slots, error: null };
    } catch (error) {
      console.error('Get helper availability error:', error);
      return { data: [], error };
    }
  },

  /**
   * Update helper profile (for helper users)
   */
  async updateHelperProfile(helperId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case
      const dbUpdates = {};
      if (updates?.bio !== undefined) dbUpdates.bio = updates?.bio;
      if (updates?.expertise !== undefined) dbUpdates.expertise = updates?.expertise;
      if (updates?.languages !== undefined) dbUpdates.languages = updates?.languages;
      if (updates?.location !== undefined) dbUpdates.location = updates?.location;
      if (updates?.availabilityStatus !== undefined) dbUpdates.availability_status = updates?.availabilityStatus;

      const { data, error } = await supabase?.from('helpers')?.update(dbUpdates)?.eq('id', helperId)?.eq('user_id', user?.id)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update helper profile error:', error);
      return { data: null, error };
    }
  },

  /**
   * Create helper availability slot
   */
  async createAvailabilitySlot(helperId, slotData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify user owns this helper profile
      const { data: helper } = await supabase?.from('helpers')?.select('id')?.eq('id', helperId)?.eq('user_id', user?.id)?.single();
      if (!helper) throw new Error('Unauthorized');

      const dbData = {
        helper_id: helperId,
        day_of_week: slotData?.dayOfWeek,
        time_slot: slotData?.timeSlot,
        duration: slotData?.duration || 60,
        session_type: slotData?.sessionType || 'video',
        is_available: slotData?.isAvailable !== undefined ? slotData?.isAvailable : true
      };

      const { data, error } = await supabase?.from('helper_availability')?.insert(dbData)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create availability slot error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update helper availability slot
   */
  async updateAvailabilitySlot(slotId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const dbUpdates = {};
      if (updates?.dayOfWeek !== undefined) dbUpdates.day_of_week = updates?.dayOfWeek;
      if (updates?.timeSlot !== undefined) dbUpdates.time_slot = updates?.timeSlot;
      if (updates?.duration !== undefined) dbUpdates.duration = updates?.duration;
      if (updates?.sessionType !== undefined) dbUpdates.session_type = updates?.sessionType;
      if (updates?.isAvailable !== undefined) dbUpdates.is_available = updates?.isAvailable;

      const { data, error } = await supabase?.from('helper_availability')?.update(dbUpdates)?.eq('id', slotId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update availability slot error:', error);
      return { data: null, error };
    }
  },

  /**
   * Delete helper availability slot
   */
  async deleteAvailabilitySlot(slotId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('helper_availability')?.delete()?.eq('id', slotId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete availability slot error:', error);
      return { error };
    }
  },

  /**
   * Get current helper's profile
   */
  async getCurrentHelperProfile() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('helpers')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get current helper profile error:', error);
      return { data: null, error };
    }
  }
};